import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { FleetMap } from '../components/FleetMap'
import { VehicleNav } from '../components/VehicleNav'
import { IconHistory, IconPin, IconSpeed } from '../components/Icons'
import { useGetVehiclesQuery } from '../services/vehicle'
import { useGetHistoriesForDayQuery } from '../services/histories'
import type { Vehicle } from '../types'
import './History.css'

export function HistoryPage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const { data } = useGetVehiclesQuery()
  const vehicles = data?.data ?? []
  const vehicleId = params.get('vehicleId') || vehicles[0]?._id || ''
  const [day, setDay] = useState(moment().format('YYYY-MM-DD'))
  const [mode, setMode] = useState<'list' | 'map'>('list')

  const { data: hist, isFetching } = useGetHistoriesForDayQuery(
    { vehicleId, day, lean: 1, limit: 2000 },
    { skip: !vehicleId }
  )

  const rows = useMemo(
    () => [...(hist?.rows ?? [])].sort((a, b) => b.timestamp - a.timestamp),
    [hist?.rows]
  )

  const selectVehicle = (v: Vehicle) => {
    params.set('vehicleId', v._id)
    setParams(params)
  }

  return (
    <div className="history">
      <VehicleNav
        vehicles={vehicles}
        selectedId={vehicleId}
        onSelect={selectVehicle}
        calendar="day"
        day={day}
        onDay={setDay}
        mode={mode}
        onMode={setMode}
      />

      <div className="history__body">
        <p className="history__day-label">{moment(day).format('dddd DD MMMM YYYY')}</p>

        {isFetching ? (
          <p className="muted">{t('Chargement…')}</p>
        ) : rows.length === 0 ? (
          <div className="history__empty">
            <IconHistory size={32} />
            <h2>{t('Pas de trajet')}</h2>
          </div>
        ) : mode === 'map' ? (
          <FleetMap path={rows} height="min(70vh, 640px)" />
        ) : (
          <div className="history__timeline">
            {rows.slice(0, 200).map((r, i) => (
              <div key={r._id} className="history__event">
                <div className="history__time">
                  <strong>{moment(r.timestamp).format('HH:mm')}</strong>
                  <span className={`history__dot${i === 0 ? ' is-live' : ''}`} />
                </div>
                <div className="history__card">
                  <div className="history__meta">
                    <IconSpeed size={14} />
                    {Number.isFinite(r.speed) ? `${Math.round(r.speed)} km/h` : '—'}
                  </div>
                  {r.address && (
                    <p>
                      <IconPin size={14} />
                      {r.address}
                    </p>
                  )}
                  <small>
                    {r.latitude?.toFixed(5)}, {r.longitude?.toFixed(5)}
                  </small>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
